import { ContentCollectionResponse, ContentCollectionType, EntitlementType, Top10CountryInfoResponse } from '../API';
import { uniq } from 'lodash-es';

/**
 * One row per (published Top 10 collection × country). The dedicated
 * getTop10CountriesInfo endpoint often returns at most one row per country, which
 * breaks tier conflict logic when several collections share the same country.
 */
export function top10PublishedCollectionsToCountryInfoRows(
  collections: ContentCollectionResponse[]
): Top10CountryInfoResponse[] {
  const rows: Top10CountryInfoResponse[] = [];
  for (const col of collections) {
    if (col.collectionType !== ContentCollectionType.TOP_10) continue;
    const countries = col.countries?.filter((c): c is string => typeof c === 'string' && c.length > 0) ?? [];
    if (countries.length === 0) continue;
    const entitlements = col.entitlements?.length ? col.entitlements.map((e) => String(e)) : undefined;
    for (const country of countries) {
      rows.push({
        country,
        entityId: col.entityId,
        entitlements
      });
    }
  }
  return rows;
}

/**
 * Appends API-only countries (no published Top 10 row yet) so the country picker
 * still lists regions the backend advertises, without overwriting tier rows built from collections.
 */
export function mergeTop10CountryInfoWithApiOnlyCountries(
  tierRows: Top10CountryInfoResponse[],
  apiRows: Top10CountryInfoResponse[]
): Top10CountryInfoResponse[] {
  const covered = new Set(tierRows.map((r) => r.country));
  const filler: Top10CountryInfoResponse[] = [];
  const fillerCountries = new Set<string>();
  for (const r of apiRows) {
    if (!r.country || covered.has(r.country)) continue;
    if (fillerCountries.has(r.country)) continue;
    fillerCountries.add(r.country);
    filler.push(r);
  }
  return [...tierRows, ...filler];
}

const ENTITLEMENT_API_VALUES = new Set<string>(Object.values(EntitlementType));

function normalizeToEntitlementType(value: unknown): EntitlementType | null {
  const v = String(value).trim().toUpperCase();
  return ENTITLEMENT_API_VALUES.has(v) ? (v as EntitlementType) : null;
}

/** Normalizes API row entitlements for form state and tier map building. */
export function normalizeTop10CountryInfoRow(
  country: Top10CountryInfoResponse & { entitlements?: Array<string | EntitlementType> }
): Top10CountryInfoResponse & { entitlements: EntitlementType[] } {
  const entitlements =
    country.entitlements
      ?.map(normalizeToEntitlementType)
      .filter((e): e is EntitlementType => e !== null && e !== undefined) ?? [];
  return {
    ...country,
    entitlements
  };
}

/**
 * Tiers reported for one API row. When the API omits `entitlements` but the row
 * is tied to a collection (`entityId`), we fall back to ANON so the UI still
 * blocks that tier. That heuristic can be wrong if the real published tiers are
 * FREE/SUB but revisions were not enriched (see CollectionsHook.getTop10CountriesInfo).
 */
function getPublishedEntitlementsForCountry(
  info: Top10CountryInfoResponse & { entitlements?: Array<string | EntitlementType> }
): EntitlementType[] {
  const listed =
    info.entitlements
      ?.map(normalizeToEntitlementType)
      .filter((e): e is EntitlementType => e !== null && e !== undefined) ?? [];
  if (listed.length > 0) return listed;
  if (info.entityId) return [EntitlementType.ANON];
  return [];
}

function getPublishedEntitlementsForCountryExcludingSelf(
  info: Top10CountryInfoResponse & { entitlements?: Array<string | EntitlementType> },
  editingEntityId: string | undefined
): EntitlementType[] {
  if (editingEntityId && info.entityId === editingEntityId) {
    return [];
  }
  return getPublishedEntitlementsForCountry(info);
}

function getMergedPublishedEntitlementsForCountry(
  allInfo: Array<Top10CountryInfoResponse & { entitlements?: Array<string | EntitlementType> }>,
  countryCode: string,
  editingEntityId: string | undefined
): EntitlementType[] {
  const rows = allInfo.filter((r) => r.country === countryCode);
  const merged: EntitlementType[] = [];
  for (const row of rows) {
    merged.push(...getPublishedEntitlementsForCountryExcludingSelf(row, editingEntityId));
  }
  return uniq(merged);
}

/** Map: ISO country code → distinct ANON / FREE / SUB already used by other Top 10 collections. */
export type PublishedTiersByCountry = Readonly<Record<string, readonly EntitlementType[]>>;

/**
 * Builds the single source of truth for “which access tiers are already taken per country”
 * (excluding the collection being edited). Use for country disable rules and comparisons.
 */
export function buildPublishedTiersByCountry(
  allInfo: Array<Top10CountryInfoResponse & { entitlements?: Array<string | EntitlementType> }>,
  editingEntityId: string | undefined
): PublishedTiersByCountry {
  const countryCodes = uniq(allInfo.map((r) => r.country));
  const result: Record<string, EntitlementType[]> = {};
  for (const countryCode of countryCodes) {
    result[countryCode] = getMergedPublishedEntitlementsForCountry(allInfo, countryCode, editingEntityId);
  }
  return result;
}
